import { actionCreator } from '@ardoq/rxbeach';
import type { Transaction } from '@ardoq/profiling';
import type {
  DockedGridEditorState,
  GlobalTrackingMetadata,
  GridEditorState,
  PopoutGridEditorState,
  GridEditorData,
} from './types';
import {
  APIComponentAttributes,
  ArdoqId,
  FilterAttributes,
} from '@ardoq/api-types';
import { ContextShape } from '@ardoq/data-model';
import type { DynamicFilterStates } from 'streams/dynamicFilters/types';
import type { TableDimensionsState } from './observables/tableDimensions';
import { CurrentUserPermissionsState } from '../streams/currentUserPermissions/types';

export interface PayloadDropdownOpenerSource {
  readonly src: number;
}

export interface PayloadWorkspaceId {
  readonly workspaceId: ArdoqId;
}

export interface PayloadSetDisabledWorkspaces {
  readonly disabledWorkspaces: Set<ArdoqId>;
}

export const gridEditorResetState = actionCreator('[gridEditor] RESET_STATE');

/**
 * While the standalone Grid Editor app is unmounted (think popout) an instance
 * of gridEditor$ runs in ardoq-front. This action synchronizes these two states
 * so that the new gridEditor$ is caught up with the changes.
 */
export const initGridEditorState = actionCreator<InitGridEditorPayload>(
  '[gridEditor] INIT_GRID_EDITOR_STATE'
);

type InitDockedGridEditorPayload = {
  activeFrameId: DockedGridEditorState['activeFrameId'];
  activeViewType: DockedGridEditorState['activeViewType'];
  dockedEditorPaneStyle: DockedGridEditorState['dockedEditorPaneStyle'];
  dockedGridEditorVisibility: DockedGridEditorState['dockedGridEditorVisibility'];
};
type InitPopoutGridEditorPayload = {
  activeFrameId: PopoutGridEditorState['activeFrameId'];
  activeViewType: PopoutGridEditorState['activeViewType'];
};

export type InitGridEditorPayload =
  | InitDockedGridEditorPayload
  | InitPopoutGridEditorPayload;

export const notifyContextChange = actionCreator<ContextShape>(
  '[gridEditor] CONTEXT_CHANGE'
);

export const notifyPermissionsChange =
  actionCreator<CurrentUserPermissionsState>('[gridEditor] PERMISSIONS_CHANGE');

export const notifyGlobalTrackingMetadataChange =
  actionCreator<GlobalTrackingMetadata>(
    '[gridEditor] GLOBAL_TRACKING_METADATA_CHANGE'
  );

/**
 * Dispatched when scope data changes, and carries the underlying data that
 * defines scope data.
 */
export const notifyScopeDataChange = actionCreator<GridEditorData>(
  '[gridEditor] SCOPE_DATA_CHANGE'
);

/**
 * Dispatched when scope data is loaded into backbone and sync streams
 */
export const notifyScopeDataLoaded = actionCreator<GridEditorData>(
  '[gridEditor] SCOPE_DATA_LOADED'
);
export interface GridEditorNotifyFiltersChangePayload {
  dynamicFilterStates: DynamicFilterStates;
  filters: FilterAttributes[];
}
/**
 * Dispatched from ardoq-front when filters are changed
 */
export const notifyGridEditorFiltersChanged =
  actionCreator<GridEditorNotifyFiltersChangePayload>(
    '[gridEditor] FILTERS_CHANGE'
  );
/**
 * Dispatched when filters are loaded into backbone and sync streams
 * in the standalone GridEditor
 */
export const notifyGridEditorFiltersLoaded =
  actionCreator<GridEditorNotifyFiltersChangePayload>(
    '[gridEditor] FILTERS_LOADED'
  );

export const showGridEditor = actionCreator('[gridEditor] SHOW_GRID_EDITOR');

export const hideGridEditor = actionCreator('[gridEditor] HIDE_GRID_EDITOR');

export const requestPopoutGridEditor = actionCreator(
  '[gridEditor] REQUEST_POPOUT'
);

export const notifyPopoutGridEditorOpened = actionCreator(
  '[gridEditor] NOTIFY_POPOUT_OPENED'
);

export const notifyOpenPopoutGridEditorFailed = actionCreator(
  '[gridEditor] NOTIFY_OPEN_POPOUT_FAILED'
);

export const requestClosePopoutGridEditor = actionCreator(
  '[gridEditor] REQUEST_CLOSE_POPOUT'
);

export const notifyGridEditorFirstPaintFinished = actionCreator(
  '[gridEditor] FIRST_PAINT'
);

export const gridEditorTableLoaded = actionCreator(
  '[gridEditor] GRID_EDITOR_TABLE_LOADED'
);

export const activateDropdownState = actionCreator<PayloadDropdownOpenerSource>(
  '[gridEditor] ACTIVATE_DROPDOWN_STATE'
);

export const activateSelectFieldDropdown = actionCreator(
  '[gridEditor] ACTIVATE_SELECT_FIELD_DROPDOWN'
);

export const deactivateDropdownState = actionCreator(
  '[gridEditor] DEACTIVATE_DROPDOWN_STATE'
);

/* Set the "focus" (eg. z-index) of the docked Grid Editor's iframe */
export const setDockedGridEditorFocus = actionCreator<
  DockedGridEditorState['dockedGridEditorFocus']
>('[gridEditor] SET_DOCKED_GRID_EDITOR_FOCUS');

export const setDockedGridEditorStyle = actionCreator<
  DockedGridEditorState['dockedEditorPaneStyle']
>('[gridEditor] DOCKED_EDITOR_STYLE');

export const toggleFullscreen = actionCreator('[gridEditor] TOGGLE_FULLSCREEN');

export const setActiveViewTypeToComponent = actionCreator(
  '[gridEditor] SET_ACTIVE_VIEW_TYPE_COMPONENT'
);

export const setActiveViewTypeToReference = actionCreator(
  '[gridEditor] SET_ACTIVE_VIEW_TYPE_REFERENCE'
);

/**
 * Used to load components into the GridEditor's componentHierarchy state
 */
export const notifyGridEditorCreatedComponents = actionCreator<
  APIComponentAttributes[]
>('[gridEditor] NOTIFY_GRID_EDITOR_CREATED_COMPONENTS');

export const forceRebuildComponentHierarchy = actionCreator(
  '[gridEditor] FORCE_REBUILD_COMPONENT_HIERARCHY'
);

export const toggleShowAllComponents = actionCreator(
  '[gridEditor] EDITOR_CONTEXT_MANAGER_SET_IS_SHOW_ALL_COMPONENTS'
);

export const gridEditorFetchOrgWorkspaces = actionCreator(
  '[gridEditor] FETCH_ORG_WORKSPACES'
);

export const gridEditorLoadWorkspace = actionCreator<PayloadWorkspaceId>(
  '[gridEditor] LOAD_WORKSPACE'
);

export const gridTargetWorkspaceEnable = actionCreator<PayloadWorkspaceId>(
  '[gridEditor] GRID_TARGET_WORKSPACE_ENABLE'
);

export const gridTargetWorkspaceDisable = actionCreator<PayloadWorkspaceId>(
  '[gridEditor] GRID_TARGET_WORKSPACE_DISABLE'
);

export const gridSetDisabledWorkspaces =
  actionCreator<PayloadSetDisabledWorkspaces>(
    '[gridEditor] GRID_SET_DISABLED_WORKSPACES'
  );

/**
 * Initialize hidden/disabled fields in the Grid Editor.
 * This is a separate action than "set" because it should not try to persist the
 * change to user-settings.
 */
export const gridEditorInitDisabledFields =
  actionCreator<DisabledFieldsPayload>(
    '[gridEditor] GRID_INIT_DISABLED_FIELDS'
  );

export type DisabledFieldsPayload = { disabledFields: Set<string> };

/**
 * Enable (show) or disable (hide) fields.
 * Use when you want to keep existing disabled fields
 */
export const gridFieldBatchUpdate = actionCreator<BatchUpdateFieldsPayload>(
  '[gridEditor] GRID_FIELD_BATCH_UPDATE'
);

export type BatchUpdateFieldsPayload = {
  enable?: string[];
  disable?: string[];
};

/**
 * Enable (show) all displayable fields
 */
export const gridEnableAllFields = actionCreator(
  '[gridEditor] GRID_ENABLE_ALL_FIELDS'
);

/**
 * Sort rows by column (eg. field value)
 */
export const requestColumnSort = actionCreator<SetColumnSortPayload>(
  '[gridEditor] REQUEST_COLUMN_SORT'
);

export type SetColumnSortPayload = { fieldName: string };

export type SetTableDimensionsPayload = {
  fieldWidths: TableDimensionsState['fieldWidths'];
  rowCount: TableDimensionsState['rowCount'];
  tableWidth: TableDimensionsState['tableWidth'];
};
export const setTableDimensions = actionCreator<SetTableDimensionsPayload>(
  '[gridEditor] SET_TABLE_DIMENSIONS'
);

export const setScrollLeft = actionCreator<number>(
  '[gridEditor] SET_SCROLL_LEFT'
);

/**
 * Only added for testing purposes
 */
export const resetTableDimensions = actionCreator(
  '[gridEditor] RESET_TABLE_DIMENSIONS'
);

export const blockPopoutGridEditor = actionCreator(
  '[gridEditor] BLOCK_POPOUT_GRID_EDITOR'
);

export const endGridEditorStartupTransaction = actionCreator<{
  transaction: Transaction;
}>('[gridEditor] END_GRID_EDITOR_STARTUP_TRANSACTION');

export const gridEditorCrashed = actionCreator<{
  error: Error;
  gridEditorModel: GridEditorState;
}>('[gridEditor] GRID_EDITOR_CRASHED');
